import { useState, useRef, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardFooter, Button, Input } from '../../../shared/components/ui';
import { useBillData, useUploadData, useDeleteBill } from '../../../hooks/useProjectData';
import { FileText, CheckCircle, AlertCircle, Receipt, Calculator, X, Eye, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'react-hot-toast';
import { useExchangeRate, currencyUtils } from '../../../hooks/useExchangeRate';
import { useProjectExchangeRate, useUpdateProjectExchangeRate } from '../../../hooks/useProjectExchangeRate';
import { useTranslation } from 'react-i18next';
import { useFileUpload } from '../../../shared/hooks/useFileUpload';
import { formatCurrency } from '../../../shared/utils/uploadHelpers';

interface BillUploadProps {
  projectId?: string;
}

interface ManualBillData {
  period: string;
  consumptionKwh: number;
  energyRate: number;
  energyCharge: number;
  demandHpKw: number;
  demandHpRate: number;
  demandHpCharge: number;
  demandHfpKw: number;
  demandHfpRate: number;
  demandHfpCharge: number;
  transmission: number;
  lgse: number;
  publicService: number;
  publicServiceFet: number;
  pdxEnergy: number;
  pdxDemand: number;
  complementary: number;
  technicalMin: number;
  powerPurchase: number;
  otherCharges: number;
  subtotal: number;
  iva: number;
  total: number;
}

// Helper component for compact input fields
const BillInput = ({
  label,
  value,
  field,
  onChange,
  required = false,
  placeholder = '',
  type = 'number'
}: {
  label: string;
  value: string | number;
  field: keyof ManualBillData;
  onChange: (field: keyof ManualBillData, value: string) => void;
  required?: boolean;
  placeholder?: string;
  type?: string;
}) => (
  <div>
    <label className="text-xs text-swiss-stone">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    <Input
      type={type}
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => onChange(field, e.target.value)}
      className={`mt-1 text-xs h-7 ${required && (!value || value === 0) ? 'border-red-300' : ''}`}
      required={required}
    />
  </div>
);

// Helper function to map extracted data to ManualBillData
const mapExtractedData = (extractedData: any): ManualBillData => ({
  period: extractedData.billing_period || extractedData.period || '',
  consumptionKwh: extractedData.total_consumption_kwh || extractedData.consumptionKwh || 0,
  energyRate: extractedData.energy_rate_clp_kwh || extractedData.energyRate ||
    (extractedData.energy_charge_clp && extractedData.total_consumption_kwh
      ? extractedData.energy_charge_clp / extractedData.total_consumption_kwh : 0),
  energyCharge: extractedData.energy_charge_clp || extractedData.energyCharge || 0,
  demandHpKw: extractedData.demand_hp_kw || extractedData.demanda_hora_punta_kw || extractedData.peak_demand_kw || extractedData.demandHpKw || 0,
  demandHpRate: extractedData.demand_hp_rate_clp_per_kw || extractedData.demanda_hora_punta_clp_per_kw || extractedData.demand_charge_clp_kw || extractedData.demandHpRate || 0,
  demandHpCharge: extractedData.demand_hp_charge_clp || extractedData.demanda_hora_punta_clp || extractedData.demandHpCharge || 0,
  demandHfpKw: extractedData.demand_hfp_kw || extractedData.demanda_maxima_kw || extractedData.demandHfpKw || 0,
  demandHfpRate: extractedData.demand_hfp_rate_clp_per_kw || extractedData.demanda_maxima_clp_per_kw || extractedData.demandHfpRate || 0,
  demandHfpCharge: extractedData.demand_hfp_charge_clp || extractedData.demanda_maxima_clp || extractedData.demandHfpCharge || 0,
  transmission: extractedData.transmission_charges_clp || extractedData.transmission || 0,
  lgse: extractedData.lgse_charge_clp || extractedData.lgse || 0,
  publicService: extractedData.public_service_charge_clp || extractedData.public_service || 0,
  publicServiceFet: extractedData.public_service_fet_clp || extractedData.public_service_fet || 0,
  pdxEnergy: extractedData.energy_pdx_clp || extractedData.pdx_energy || 0,
  pdxDemand: extractedData.demand_pdx_clp || extractedData.pdx_demand || 0,
  complementary: extractedData.complementary_services_clp || extractedData.complementary || 0,
  technicalMin: extractedData.technical_minimum_clp || extractedData.technical_min || 0,
  powerPurchase: extractedData.power_purchase_clp || extractedData.power_purchase || 0,
  otherCharges: extractedData.other_charges_clp || extractedData.other_charges || 0,
  subtotal: extractedData.subtotal_clp || extractedData.subtotal || 0,
  iva: extractedData.vat_clp || extractedData.iva || 0,
  total: extractedData.total_clp || extractedData.total || 0,
});

// Initial empty bill data
const INITIAL_BILL_DATA: ManualBillData = {
  period: '',
  consumptionKwh: 0,
  energyRate: 0,
  energyCharge: 0,
  demandHpKw: 0,
  demandHpRate: 0,
  demandHpCharge: 0,
  demandHfpKw: 0,
  demandHfpRate: 0,
  demandHfpCharge: 0,
  transmission: 0,
  lgse: 0,
  publicService: 0,
  publicServiceFet: 0,
  pdxEnergy: 0,
  pdxDemand: 0,
  complementary: 0,
  technicalMin: 0,
  powerPurchase: 0,
  otherCharges: 0,
  subtotal: 0,
  iva: 0,
  total: 0,
};

export function BillUploadEnhanced({ projectId }: BillUploadProps) {
  const { t } = useTranslation();

  // File upload hook
  const fileUpload = useFileUpload({
    multiple: false,
    acceptedTypes: ['.pdf'],
    maxSizeMB: 50,
  });

  // Use React Query hooks with progress callback
  const { data: bill, isLoading: loadingData } = useBillData(projectId);
  const uploadMutation = useUploadData(projectId, (progress, step) => {
    fileUpload.setUploadProgress(progress);
    fileUpload.setUploadStep(step);
  });
  const deleteBillMutation = useDeleteBill(projectId);

  // Exchange rate hooks - use project-specific rate
  const { data: projectExchangeData } = useProjectExchangeRate(projectId);
  const updateProjectRateMutation = useUpdateProjectExchangeRate(projectId);
  const projectExchangeRate = projectExchangeData?.project_rate || 900;

  // Service exchange rate for reference
  const { data: serviceRateData } = useExchangeRate();
  const serviceExchangeRate = serviceRateData?.usd_to_clp || 900;

  // Local state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showOtherCharges, setShowOtherCharges] = useState(false);
  const [existingPdfUrl, setExistingPdfUrl] = useState<string | null>(null);
  const [useUsdMwh, setUseUsdMwh] = useState(false); // Toggle for unit conversion
  const [manualRate, setManualRate] = useState<string>('');
  const [showRateEdit, setShowRateEdit] = useState(false);
  const [isExtracted, setIsExtracted] = useState(false); // Track if data was extracted but not saved
  const [isModified, setIsModified] = useState(false); // Track if user has made changes
  const [manualData, setManualData] = useState<ManualBillData>(INITIAL_BILL_DATA);
  const [billType, setBillType] = useState<string>('auto'); // Bill type selector: 'auto', 'emoac', 'enel', 'energy_asset'

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoize PDF URL to prevent iframe reloads
  const pdfPreviewUrl = useMemo(() => {
    if (fileUpload.files[0]) {
      return URL.createObjectURL(fileUpload.files[0]);
    } else if (existingPdfUrl) {
      return existingPdfUrl;
    }
    return null;
  }, [fileUpload.files, existingPdfUrl]);

  // Cleanup blob URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Check for existing PDF when bill data loads
  useEffect(() => {
    if (bill?.hasPdf && projectId && !existingPdfUrl) {
      setExistingPdfUrl(`/api/projects/${projectId}/bill/pdf`);
      setShowPdfPreview(true);
    }
  }, [bill?.hasPdf, projectId, existingPdfUrl]);

  // Update manual data when bill data changes (auto-fill from extracted data)
  useEffect(() => {
    if (bill) {
      setManualData(mapExtractedData(bill));
    }
  }, [bill]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    fileUpload.handleFileSelect(e);
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setShowPdfPreview(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    fileUpload.handleDrop(e);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setShowPdfPreview(true);
    } else if (droppedFile) {
      fileUpload.setUploadError(t('billUpload.errors.pdfOnly'));
    }
  };

  // STEP 1: Extract from PDF (NO SAVE)
  const extractFromPDF = async () => {
    if (fileUpload.files.length === 0) {
      fileUpload.setUploadError(t('billUpload.errors.selectFile'));
      return;
    }

    if (!projectId) {
      fileUpload.setUploadError(t('billUpload.errors.selectProject'));
      return;
    }

    fileUpload.setUploadError(null);
    fileUpload.setUploadProgress(0);
    fileUpload.setUploadStep(t('billUpload.status.startingUpload'));

    try {
      const result = await uploadMutation.mutateAsync({
        file: fileUpload.files[0],
        type: 'bill',
        billType: billType // Pass selected bill type to backend
      });

      // Update manual data with extracted values
      if (result.data || result.bill || result.success) {
        const extractedData = result.data || result.bill || result;

        // Save exchange rate to project if it exists in the bill data
        if (extractedData.exchange_rate && projectId) {
          try {
            await updateProjectRateMutation.mutateAsync(extractedData.exchange_rate);
            toast.success(t('billUpload.toast.rateUpdated', { rate: extractedData.exchange_rate }));
          } catch (error) {
            console.error('Failed to update project exchange rate:', error);
          }
        }

        setManualData(mapExtractedData(extractedData));
        setIsExtracted(true);
        setIsModified(false);
        fileUpload.setUploadProgress(0);
        fileUpload.setUploadStep('');
        // Note: Currently this still saves to backend due to existing endpoint behavior
        // In future, we'd have a separate extract-only endpoint
        toast.success(t('billUpload.toast.extractSuccess'));
      }
    } catch (error) {
      console.error('Error extracting bill:', error);
      fileUpload.setUploadError(error instanceof Error ? error.message : t('billUpload.errors.extractFailed'));
      fileUpload.setUploadProgress(0);
      fileUpload.setUploadStep('');
    }
  };

  // STEP 2: Validate required fields
  const validateBillData = (): string | null => {
    if (!manualData.consumptionKwh || manualData.consumptionKwh === 0) {
      return t('billUpload.validation.consumptionRequired');
    }
    if (!manualData.energyRate || manualData.energyRate === 0) {
      return t('billUpload.validation.energyRateRequired');
    }
    if (!manualData.demandHpKw || manualData.demandHpKw === 0) {
      return t('billUpload.validation.demandHpRequired');
    }
    if (!manualData.demandHpRate || manualData.demandHpRate === 0) {
      return t('billUpload.validation.demandHpRateRequired');
    }
    return null;
  };

  // STEP 3: Single save operation
  const saveBillData = async () => {
    if (!projectId) {
      fileUpload.setUploadError(t('billUpload.errors.selectProject'));
      return;
    }

    // Validate first
    const validationError = validateBillData();
    if (validationError) {
      fileUpload.setUploadError(validationError);
      toast.error(validationError);
      return;
    }

    fileUpload.setUploadError(null);

    try {
      // Create JSON and upload
      const jsonString = JSON.stringify(manualData);
      const jsonBlob = new Blob([jsonString], { type: 'application/json' });
      const jsonFile = new File([jsonBlob], 'bill-data.json', { type: 'application/json' });

      const result = await uploadMutation.mutateAsync({
        file: jsonFile,
        type: 'bill'
      });

      setIsModified(false);
      toast.success(t('billUpload.toast.saveSuccess'));
    } catch (error) {
      console.error('Error saving bill:', error);
      fileUpload.setUploadError(error instanceof Error ? error.message : t('billUpload.errors.saveFailed'));
    }
  };

  const clearBill = async () => {
    try {
      // Delete bill from backend
      await deleteBillMutation.mutateAsync();

      // Clear local state
      fileUpload.clearFiles();
      fileUpload.setUploadError(null);
      setShowPdfPreview(false);
      setExistingPdfUrl(null);
      setManualData(INITIAL_BILL_DATA);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success(t('billUpload.toast.clearSuccess'));
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast.error(t('billUpload.toast.clearFailed'));
    }
  };

  const handleManualInputChange = (field: keyof ManualBillData, value: string) => {
    const numValue = field === 'period' ? value : (parseFloat(value) || 0);
    const updatedData = { ...manualData, [field]: numValue };

    // Auto-calculate charges if rates and quantities are provided
    if (field === 'energyRate' || field === 'consumptionKwh') {
      const energy = parseFloat(updatedData.energyRate.toString()) * parseFloat(updatedData.consumptionKwh.toString());
      updatedData.energyCharge = isNaN(energy) ? 0 : energy;
    }

    if (field === 'demandHpKw' || field === 'demandHpRate') {
      const demandHp = parseFloat(updatedData.demandHpKw.toString()) * parseFloat(updatedData.demandHpRate.toString());
      updatedData.demandHpCharge = isNaN(demandHp) ? 0 : demandHp;
    }

    if (field === 'demandHfpKw' || field === 'demandHfpRate') {
      const demandHfp = parseFloat(updatedData.demandHfpKw.toString()) * parseFloat(updatedData.demandHfpRate.toString());
      updatedData.demandHfpCharge = isNaN(demandHfp) ? 0 : demandHfp;
    }

    setManualData(updatedData);
    setIsModified(true); // Mark as modified when user changes data
    autoCalculateTotals(updatedData);
  };

  const autoCalculateTotals = (data: ManualBillData) => {
    // Always recalculate totals from components when values change
    // This ensures that manual edits to rates/charges update the subtotal and total
    const subtotal = (
      (data.energyCharge || 0) +
      (data.demandHpCharge || 0) +
      (data.demandHfpCharge || 0) +
      (data.transmission || 0) +
      (data.lgse || 0) +
      (data.publicService || 0) +
      (data.publicServiceFet || 0) +
      (data.pdxEnergy || 0) +
      (data.pdxDemand || 0) +
      (data.complementary || 0) +
      (data.technicalMin || 0) +
      (data.powerPurchase || 0) +
      (data.otherCharges || 0)
    );

    // Calculate IVA (19% tax) on the subtotal
    const iva = subtotal * 0.19;

    // Total = subtotal + tax
    const total = subtotal + iva;

    setManualData(prev => ({ ...prev, subtotal, iva, total }));
  };

  const isLoading = uploadMutation.isPending || loadingData || deleteBillMutation.isPending;

  return (
    <Card variant={bill ? 'elevated' : 'default'}>
      <CardContent className="p-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: PDF Upload or Preview */}
          <div className="space-y-4">
            {/* Bill Type Selector */}
            {!showPdfPreview && (
              <div className="bg-white rounded-lg border border-swiss-mist p-4">
                <label className="text-sm font-medium text-swiss-charcoal mb-2 block">
                  {t('billUpload.billType.label')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBillType('auto')}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                      billType === 'auto'
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-swiss-charcoal border-swiss-mist hover:border-brand-primary'
                    )}
                  >
                    {t('billUpload.billType.auto')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillType('emoac')}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                      billType === 'emoac'
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-swiss-charcoal border-swiss-mist hover:border-brand-primary'
                    )}
                  >
                    {t('billUpload.billType.emoac')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillType('enel')}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                      billType === 'enel'
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-swiss-charcoal border-swiss-mist hover:border-brand-primary'
                    )}
                  >
                    {t('billUpload.billType.enel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillType('energy_asset')}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                      billType === 'energy_asset'
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-swiss-charcoal border-swiss-mist hover:border-brand-primary'
                    )}
                  >
                    {t('billUpload.billType.energyAsset')}
                  </button>
                </div>
                <p className="text-xs text-swiss-stone mt-2">
                  {t('billUpload.billType.description')}
                </p>
              </div>
            )}

            {!showPdfPreview ? (
              /* Upload Zone - Clickable */
              <div
                className={clsx(
                  'border-2 border-dashed rounded-lg p-16 text-center transition-colors cursor-pointer',
                  'hover:border-brand-primary hover:bg-brand-primary/5',
                  fileUpload.files.length > 0 ? 'border-brand-primary bg-brand-primary/5' : 'border-swiss-mist'
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={fileUpload.handleDragOver}
                onDrop={handleDrop}
              >
                <Receipt className="w-12 h-12 mx-auto text-swiss-stone mb-4" />
                <p className="text-sm font-medium text-swiss-charcoal mb-2">
                  {fileUpload.files[0] ? fileUpload.files[0].name : t('billUpload.upload.clickToUpload')}
                </p>
                <p className="text-xs text-swiss-stone">
                  {fileUpload.files[0] ? t('billUpload.upload.clickToChange') : t('billUpload.upload.dragAndDrop')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="bill-file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : null}

            {/* File Info - only show when not in preview mode */}
            {fileUpload.files[0] && !showPdfPreview && (
              <div className="bg-white rounded-lg border border-swiss-mist p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-brand-primary" />
                    <span className="text-sm text-swiss-charcoal">{fileUpload.files[0].name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPdfPreview(true)}
                      className="text-brand-primary hover:text-brand-primary/80"
                      title={t('billUpload.upload.previewPdf')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        fileUpload.clearFiles();
                        setShowPdfPreview(false);
                      }}
                      className="text-swiss-stone hover:text-red-500"
                      title={t('billUpload.upload.removeFile')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced PDF Preview - Full height */}
            {showPdfPreview && (fileUpload.files[0] || existingPdfUrl) && (
              <div className="bg-white rounded-lg border border-swiss-mist">
                <div className="flex items-center justify-between p-3 border-b border-swiss-mist">
                  <span className="text-sm font-medium text-swiss-charcoal">
                    {t('billUpload.upload.billPdf')}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPdfPreview(false)}
                      className="text-swiss-stone hover:text-swiss-charcoal"
                      title={t('billUpload.upload.hidePreview')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        fileUpload.clearFiles();
                        setShowPdfPreview(false);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="text-swiss-stone hover:text-red-500"
                      title={t('billUpload.upload.removeFile')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="bg-swiss-pearl" style={{ height: '800px' }}>
                  {pdfPreviewUrl && (
                    <iframe
                      key={pdfPreviewUrl}
                      src={pdfPreviewUrl}
                      className="w-full h-full"
                      title="PDF Preview"
                    />
                  )}
                </div>
              </div>
            )}

            {fileUpload.uploadError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{fileUpload.uploadError}</span>
              </div>
            )}

            {/* Upload Progress Bar */}
            {uploadMutation.isPending && fileUpload.uploadProgress > 0 && (
              <div className="space-y-2 p-4 bg-brand-primary/5 rounded-lg border border-brand-primary/20">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-swiss-charcoal font-medium">{fileUpload.uploadStep}</span>
                  <span className="text-brand-primary font-semibold">{fileUpload.uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-swiss-pearl rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-primary transition-all duration-300 ease-out"
                    style={{ width: `${fileUpload.uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Manual Entry Form */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-swiss-charcoal">
                {t('billUpload.form.title')}
              </h4>
              {bill && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            {/* Basic Information */}
            <div className="bg-white rounded-lg border border-swiss-mist p-3">
              <div className="text-xs font-semibold text-swiss-graphite mb-2">
                📋 {t('billUpload.form.basicInfo')}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-swiss-stone">{t('billUpload.labels.period')}:</label>
                  <Input
                    type="text"
                    placeholder={t('billUpload.labels.periodPlaceholder')}
                    value={manualData.period}
                    onChange={(e) => handleManualInputChange('period', e.target.value)}
                    className="mt-1 text-xs h-8"
                  />
                </div>
                <div>
                  <label className="text-xs text-swiss-stone">
                    {t('billUpload.labels.totalKwh')}: <span className="text-red-500">* {t('billUpload.labels.required')}</span>
                  </label>
                  <Input
                    type="number"
                    placeholder={t('billUpload.labels.enterKwh')}
                    value={manualData.consumptionKwh || ''}
                    onChange={(e) => handleManualInputChange('consumptionKwh', e.target.value)}
                    className={`mt-1 text-xs h-8 ${!manualData.consumptionKwh || manualData.consumptionKwh === 0 ? 'border-red-300' : ''}`}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Exchange Rate */}
            <div className="bg-white rounded-lg border border-swiss-mist p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-brand-primary" />
                  <span className="text-xs font-semibold text-swiss-graphite">{t('billUpload.exchangeRate.title')}</span>
                </div>
                {!showRateEdit ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-swiss-charcoal">
                      1 USD = {projectExchangeRate} CLP
                    </span>
                    <button
                      onClick={() => {
                        setShowRateEdit(true);
                        setManualRate(projectExchangeRate.toString());
                      }}
                      className="text-xs text-brand-primary hover:text-brand-primary/80 underline"
                      disabled={!projectId}
                    >
                      {t('billUpload.buttons.edit')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={manualRate}
                      onChange={(e) => setManualRate(e.target.value)}
                      className="w-24 text-xs h-7"
                      placeholder="900"
                    />
                    <button
                      onClick={async () => {
                        const newRate = parseFloat(manualRate);
                        if (newRate > 0 && newRate <= 2000 && projectId) {
                          await updateProjectRateMutation.mutateAsync(newRate);
                          setShowRateEdit(false);
                          toast.success(t('billUpload.exchangeRate.rateSet', { rate: newRate }));
                        } else if (!projectId) {
                          toast.error(t('billUpload.errors.selectProject'));
                        } else {
                          toast.error(t('billUpload.exchangeRate.invalidRate'));
                        }
                      }}
                      className="text-xs text-green-600 hover:text-green-700"
                    >
                      {t('billUpload.buttons.save')}
                    </button>
                    <button
                      onClick={() => {
                        setShowRateEdit(false);
                        setManualRate('');
                      }}
                      className="text-xs text-swiss-stone hover:text-red-500"
                    >
                      {t('billUpload.buttons.cancel')}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-swiss-stone">
                  {projectExchangeData?.project_rate_updated
                    ? t('billUpload.exchangeRate.updated', { date: new Date(projectExchangeData.project_rate_updated).toLocaleDateString() })
                    : t('billUpload.exchangeRate.defaultRate')}
                </p>
                {projectExchangeData?.service_rate && projectExchangeData.service_rate !== projectExchangeRate && (
                  <p className="text-xs text-swiss-stone">
                    {t('billUpload.exchangeRate.serviceRate', { rate: projectExchangeData.service_rate })}
                  </p>
                )}
              </div>
            </div>

            {/* Energy Charges */}
            <div className="bg-white rounded-lg border border-swiss-mist p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-swiss-graphite">
                  ⚡ {t('billUpload.form.energyCharges')} <span className="text-red-500">* {t('billUpload.labels.required')}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setUseUsdMwh(!useUsdMwh)}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-swiss-pearl rounded hover:bg-swiss-mist transition-colors"
                >
                  <span className={clsx('font-medium', !useUsdMwh && 'text-brand-primary')}>CLP/kWh</span>
                  <span className="text-swiss-stone">|</span>
                  <span className={clsx('font-medium', useUsdMwh && 'text-brand-primary')}>USD/MWh</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-swiss-stone">
                    {t('billUpload.labels.rate')} ({useUsdMwh ? 'USD/MWh' : 'CLP/kWh'}): <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step={useUsdMwh ? "0.01" : "0.1"}
                    placeholder={useUsdMwh ? "e.g., 70" : "e.g., 46.1"}
                    value={
                      useUsdMwh 
                        ? (manualData.energyRate ? currencyUtils.clpKwhToUsdMwh(manualData.energyRate, projectExchangeRate).toFixed(2) : '')
                        : (manualData.energyRate || '')
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (useUsdMwh) {
                        // Convert USD/MWh to CLP/kWh using project rate
                        const clpKwh = value ? currencyUtils.usdMwhToClpKwh(parseFloat(value), projectExchangeRate) : 0;
                        handleManualInputChange('energyRate', clpKwh.toString());
                      } else {
                        handleManualInputChange('energyRate', value);
                      }
                    }}
                    className={`mt-1 text-xs h-8 ${!manualData.energyRate || manualData.energyRate === 0 ? 'border-red-300' : ''}`}
                    required
                  />
                  {manualData.energyRate > 0 && (
                    <p className="text-xs text-swiss-stone mt-1">
                      = {useUsdMwh
                        ? `${manualData.energyRate.toFixed(1)} CLP/kWh`
                        : `${currencyUtils.clpKwhToUsdMwh(manualData.energyRate, projectExchangeRate).toFixed(2)} USD/MWh`}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-swiss-stone">{t('billUpload.labels.totalEnergy')} (CLP):</label>
                  <Input
                    type="number"
                    placeholder={t('billUpload.labels.enterCharge')}
                    value={manualData.energyCharge || ''}
                    onChange={(e) => handleManualInputChange('energyCharge', e.target.value)}
                    className="mt-1 text-xs h-8"
                  />
                </div>
              </div>
            </div>

            {/* Demand Charges */}
            <div className="bg-white rounded-lg border border-swiss-mist p-3">
              <div className="text-xs font-semibold text-swiss-graphite mb-2">
                📈 {t('billUpload.form.demandCharges')}
              </div>
              <div className="space-y-2">
                {/* Peak Hours (HP) */}
                <div>
                  <label className="text-xs text-swiss-graphite">
                    {t('billUpload.labels.peakHours')} (HP): <span className="text-red-500">* {t('billUpload.labels.required')}</span>
                  </label>
                  <div className="grid grid-cols-3 gap-1">
                    <div>
                      <span className="text-xs text-swiss-stone">
                        kW <span className="text-red-500">*</span>
                      </span>
                      <Input
                        type="number"
                        placeholder="HP kW"
                        value={manualData.demandHpKw || ''}
                        onChange={(e) => handleManualInputChange('demandHpKw', e.target.value)}
                        className={`mt-1 text-xs h-7 ${!manualData.demandHpKw || manualData.demandHpKw === 0 ? 'border-red-300' : ''}`}
                        required
                      />
                    </div>
                    <div>
                      <span className="text-xs text-swiss-stone">
                        CLP/kW <span className="text-red-500">*</span>
                      </span>
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={manualData.demandHpRate || ''}
                        onChange={(e) => handleManualInputChange('demandHpRate', e.target.value)}
                        className={`mt-1 text-xs h-7 ${!manualData.demandHpRate || manualData.demandHpRate === 0 ? 'border-red-300' : ''}`}
                        required
                      />
                    </div>
                    <div>
                      <span className="text-xs text-swiss-stone">{t('billUpload.labels.totalClp')}</span>
                      <Input
                        type="number"
                        placeholder={t('billUpload.labels.charge')}
                        value={manualData.demandHpCharge || ''}
                        onChange={(e) => handleManualInputChange('demandHpCharge', e.target.value)}
                        className="mt-1 text-xs h-7"
                      />
                    </div>
                  </div>
                </div>
                {/* Off-Peak (HFP) */}
                <div>
                  <label className="text-xs text-swiss-graphite">{t('billUpload.labels.offPeak')} (HFP):</label>
                  <div className="grid grid-cols-3 gap-1">
                    <div>
                      <span className="text-xs text-swiss-stone">kW</span>
                      <Input
                        type="number"
                        placeholder="HFP kW"
                        value={manualData.demandHfpKw || ''}
                        onChange={(e) => handleManualInputChange('demandHfpKw', e.target.value)}
                        className="mt-1 text-xs h-7"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-swiss-stone">CLP/kW</span>
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={manualData.demandHfpRate || ''}
                        onChange={(e) => handleManualInputChange('demandHfpRate', e.target.value)}
                        className="mt-1 text-xs h-7"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-swiss-stone">{t('billUpload.labels.totalClp')}</span>
                      <Input
                        type="number"
                        placeholder={t('billUpload.labels.charge')}
                        value={manualData.demandHfpCharge || ''}
                        onChange={(e) => handleManualInputChange('demandHfpCharge', e.target.value)}
                        className="mt-1 text-xs h-7"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Charges (Collapsible) */}
            <div className="bg-white rounded-lg border border-swiss-mist">
              <button
                onClick={() => setShowOtherCharges(!showOtherCharges)}
                className="w-full p-3 text-left text-xs font-semibold text-swiss-graphite hover:bg-swiss-pearl transition-colors flex items-center justify-between"
              >
                <span>📋 {t('billUpload.form.otherCharges')}</span>
                {showOtherCharges ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showOtherCharges && (
                <div className="p-3 space-y-2 border-t border-swiss-mist">
                  {/* Transmission & Distribution */}
                  <div>
                    <div className="text-xs text-swiss-graphite font-semibold mb-1">🔌 Transmisión y Distribución:</div>
                    <div className="grid grid-cols-2 gap-1">
                      <BillInput label="Transmisión (CLP):" value={manualData.transmission} field="transmission" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="LGSE (CLP):" value={manualData.lgse} field="lgse" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                    </div>
                  </div>

                  {/* Public Service Charges */}
                  <div>
                    <div className="text-xs text-swiss-graphite font-semibold mb-1">🏢 Servicio Público:</div>
                    <div className="grid grid-cols-2 gap-1">
                      <BillInput label="Servicio Público (CLP):" value={manualData.publicService} field="publicService" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="FET (CLP):" value={manualData.publicServiceFet} field="publicServiceFet" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                    </div>
                  </div>

                  {/* PDx Charges */}
                  <div>
                    <div className="text-xs text-swiss-graphite font-semibold mb-1">📊 PDx:</div>
                    <div className="grid grid-cols-2 gap-1">
                      <BillInput label="PDx Energía (CLP):" value={manualData.pdxEnergy} field="pdxEnergy" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="PDx Demanda (CLP):" value={manualData.pdxDemand} field="pdxDemand" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                    </div>
                  </div>

                  {/* Other Service Charges */}
                  <div>
                    <div className="text-xs text-swiss-graphite font-semibold mb-1">🔧 Otros Cargos:</div>
                    <div className="grid grid-cols-2 gap-1">
                      <BillInput label="Complementarios (CLP):" value={manualData.complementary} field="complementary" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="Mínimo Técnico (CLP):" value={manualData.technicalMin} field="technicalMin" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="Compra Potencia (CLP):" value={manualData.powerPurchase} field="powerPurchase" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                      <BillInput label="Otros (CLP):" value={manualData.otherCharges} field="otherCharges" onChange={handleManualInputChange} placeholder={t('billUpload.labels.exampleValue')} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="bg-gradient-to-r from-brand-primary/5 to-brand-primary/10 rounded-lg border border-brand-primary/20 p-3">
              <div className="text-xs font-semibold text-swiss-graphite mb-2">
                💰 {t('billUpload.form.totals')}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-xs text-swiss-stone">{t('billUpload.labels.subtotal')}:</label>
                  <span className="text-xs font-medium text-swiss-charcoal">
                    {formatCurrency(manualData.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <label className="text-xs text-swiss-stone">{t('billUpload.labels.iva')}:</label>
                  <span className="text-xs font-medium text-swiss-charcoal">
                    {formatCurrency(manualData.iva)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <label className="text-xs font-semibold text-swiss-graphite">{t('billUpload.labels.total')}:</label>
                  <span className="text-sm font-bold text-brand-primary">
                    {formatCurrency(manualData.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex gap-3">
        {/* If we have a PDF file, show Extract button */}
        {fileUpload.files[0] && !isExtracted && (
          <Button
            className="flex-1"
            onClick={extractFromPDF}
            disabled={isLoading}
            loading={uploadMutation.isPending}
          >
            {t('billUpload.buttons.extractFromPdf')}
          </Button>
        )}

        {/* Save button - always visible when we have data to save */}
        {(isExtracted || manualData.period || bill) && (
          <Button
            className="flex-1"
            variant="primary"
            onClick={saveBillData}
            disabled={isLoading || (!isModified && bill)}
            loading={uploadMutation.isPending}
          >
            {bill && !isModified ? t('billUpload.buttons.dataSaved') : t('billUpload.buttons.saveBillData')}
          </Button>
        )}

        {/* Clear/Replace button when we have existing data */}
        {(bill || isExtracted) && (
          <Button
            className="flex-1"
            variant="secondary"
            onClick={clearBill}
            disabled={deleteBillMutation.isPending}
            loading={deleteBillMutation.isPending}
          >
            {t('billUpload.buttons.clearStartOver')}
          </Button>
        )}

        {/* Show status indicator */}
        {(isExtracted || isModified) && (
          <div className="flex items-center gap-2 text-sm">
            {isExtracted && !bill && (
              <span className="text-blue-600">• {t('billUpload.status.extractedNotSaved')}</span>
            )}
            {isModified && bill && (
              <span className="text-yellow-600">• {t('billUpload.status.modifiedUnsaved')}</span>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}